import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createTwoFilesPatch } from "diff";
import {
  ArrowDownToLine,
  ChevronRight,
  Check,
  ExternalLink,
  GitBranch,
  Globe,
  KeyRound,
  LogIn,
  Minus,
  MoreHorizontal,
  Plus,
  RefreshCw,
  RotateCcw,
  Unlink,
  Upload,
  Users,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { can } from "@ide/shared";
import { DiffView } from "@/components/diff-view";
import { GithubMark } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { PanelHeader } from "@/components/ui/panel-header";
import { Switch } from "@/components/ui/controls";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  Tip,
} from "@/components/ui/overlay";
import { Field, Input, Select, Spinner, Textarea } from "@/components/ui/primitives";
import { desktop } from "@/desktop/bridge";
import { useDesktopUi } from "@/desktop/ui";
import { githubAccountKey, openGitHubConnect, useGitHubAccount } from "@/desktop/github-connect";
import { del, errorMessage, get, put } from "@/lib/api";
import { cn, timeAgo } from "@/lib/utils";
import { useColoredIcons } from "@/plugins/use-plugins";
import { logSessionEvent } from "./activity";
import { stage, stagedSet, unstage, useGitChanges } from "./git-store";
import { useRole, useTree, useWs } from "./context";
import { logOutput } from "./output";
import { useWorkspace } from "./store";
import { deleteFile, writeFile } from "./file-ops";
import { fileIcon } from "./file-tree";
import {
  blobText,
  commitAndPush,
  commitFiles,
  commitHistory,
  computeChanges,
  createRepo,
  headCommit,
  importInto,
  isRemoteUrl,
  pull,
  readProjectTexts,
  remoteName,
  remoteStatus,
  repoLabel,
  repoWebUrl,
  resolveRepoInput,
  shasOf,
  type Change,
  type GitLink,
} from "./git";
import { useLocalCopy } from "./local-copy";
import { AuthorCommitsDialog } from "./author-commits";
import { openAt } from "./search-panel";
import { DOWNLOAD_URL } from "@/lib/landing";

export { useGitChanges } from "./git-store";

export const gitKeys = {
  account: githubAccountKey,
  link: (projectId: string) => ["git", "link", projectId] as const,
  remote: (repo: string, base: string) => ["git", "remote", repo, base] as const,
  history: (repo: string, branch: string) => ["git", "history", repo, branch] as const,
};

/** Conta do GitHub conectada no app (o token fica no cofre do sistema, nunca na página). */
export const useGitAccount = useGitHubAccount;

/** Repositório ligado ao projeto (comum ao time) e a base da última sincronização. */
export function useGitLink(projectId: string, enabled = true) {
  return useQuery({
    queryKey: gitKeys.link(projectId),
    queryFn: () => get<{ link: GitLink | null }>(`/projects/${projectId}/git`).then((r) => r.link),
    enabled,
    staleTime: 30_000,
  });
}

export const saveLink = (projectId: string, link: Omit<GitLink, "updatedAt" | "updatedByHandle">) =>
  put(`/projects/${projectId}/git`, {
    repo: link.repo,
    branch: link.branch,
    baseCommit: link.baseCommit,
    baseFiles: link.baseFiles,
  });

/**
 * Recalcula as alterações (SHA de blob de cada arquivo × base) ao montar, quando a árvore muda,
 * ao voltar para a janela e a cada 20 s. Vários lugares podem montar: o estado é um só.
 */
function useGitScan(link: GitLink | null | undefined) {
  const { session, detail } = useWs();
  const key = link ? `${detail.project.id}:${link.baseCommit}` : null;
  const scan = useCallback(async () => {
    if (!link || useGitChanges.getState().scanning) return;
    useGitChanges.setState({ scanning: true });
    try {
      const texts = await readProjectTexts(session);
      const changes = computeChanges(await shasOf(texts), link.baseFiles);
      // Preparado que deixou de ser alteração (desfeito, ou já commitado) sai da lista.
      const paths = new Set(changes.map((c) => c.path));
      useGitChanges.setState((s) => ({
        changes,
        key,
        staged: s.staged.filter((p) => paths.has(p)),
      }));
    } catch (err) {
      logOutput("Git", `Não foi possível ler as alterações: ${errorMessage(err)}`, "error");
    } finally {
      useGitChanges.setState({ scanning: false });
    }
  }, [session, link, key]);
  useEffect(() => {
    if (!link) return;
    if (useGitChanges.getState().key !== key) {
      const sameProject = useGitChanges.getState().key?.split(":")[0] === detail.project.id;
      useGitChanges.setState((s) => ({
        changes: null,
        key,
        staged: sameProject ? s.staged : [],
        conflicts: sameProject ? s.conflicts : [],
      }));
    }
    let timer: ReturnType<typeof setTimeout> | undefined = setTimeout(() => void scan(), 0);
    const off = session.on("tree", () => {
      clearTimeout(timer);
      timer = setTimeout(() => void scan(), 800);
    });
    const every = setInterval(() => void scan(), 20_000);
    const onFocus = () => void scan();
    window.addEventListener("focus", onFocus);
    return () => {
      off();
      clearTimeout(timer);
      clearInterval(every);
      window.removeEventListener("focus", onFocus);
    };
  }, [scan, session, link, key, detail.project.id]);
  return scan;
}

/** Commit (do que está preparado, ou de tudo) e push — o mesmo para o painel e o rodapé. */
function useGitCommit(link: GitLink | null | undefined) {
  const { detail, session, me } = useWs();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!link) throw new Error("Projeto sem repositório.");
      const texts = await readProjectTexts(session);
      const res = await commitAndPush(link, texts, useGitChanges.getState().message.trim(), stagedSet(), {
        name: me.user.name,
        email: me.user.email ?? `${me.user.handle}@users.noreply.rrmind.tech`,
      });
      await saveLink(detail.project.id, { ...link, baseCommit: res.commit, baseFiles: res.baseFiles });
      return res;
    },
    onSuccess: (res) => {
      const done = new Set(res.changes.map((c) => c.path));
      useGitChanges.setState((s) => ({ message: "", staged: s.staged.filter((p) => !done.has(p)) }));
      const text = `Commit ${res.commit.slice(0, 7)} enviado para ${link?.branch} (${res.changes.length} ${res.changes.length === 1 ? "arquivo" : "arquivos"}).`;
      toast.success(text);
      logOutput("Git", text);
      logSessionEvent({
        kind: "git",
        who: me.user.name,
        text: `fez commit e push em ${link?.branch} (${res.commit.slice(0, 7)})`,
      });
      void qc.invalidateQueries({ queryKey: gitKeys.link(detail.project.id) });
      if (link) void qc.invalidateQueries({ queryKey: gitKeys.history(link.repo, link.branch) });
    },
    onError: (err) => {
      toast.error(errorMessage(err));
      logOutput("Git", `Commit falhou: ${errorMessage(err)}`, "error");
    },
  });
}

const KIND_LABEL: Record<Change["kind"], string> = { M: "Modificado", A: "Novo", D: "Apagado" };

/** Commits no GitHub que ainda não vieram para o Kompil (consulta leve, a cada minuto). */
function useRemoteStatus(link: GitLink) {
  return useQuery({
    queryKey: gitKeys.remote(link.repo, link.baseCommit),
    queryFn: () => remoteStatus(link),
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: false,
  });
}

function SectionTitle({ children, actions }: { children: string; actions?: ReactNode }) {
  return <PanelHeader title={children} actions={actions} />;
}

function Gate({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex h-full flex-col">
      <SectionTitle>Controle do código-fonte</SectionTitle>
      <div className="px-5 py-2 text-[12.5px] leading-relaxed text-fg-muted">
        <p className="mb-1 font-medium text-fg">{title}</p>
        {children}
      </div>
    </div>
  );
}

function ConnectAccount() {
  return (
    <Gate title="Conecte sua conta do GitHub">
      <p>
        O Kompil clona, faz commit, push e pull direto no GitHub. Conecte pelo navegador — o acesso fica
        criptografado no cofre do sistema e nunca sai deste computador, a não ser para o GitHub.
      </p>
      <GitHubConnectButton />
    </Gate>
  );
}

function GitHubConnectButton() {
  return (
    <Button variant="primary" className="mt-3 w-full" onClick={openGitHubConnect}>
      <GithubMark className="h-3.5 w-3.5" /> Conectar ao GitHub
    </Button>
  );
}

/**
 * Credencial de um servidor Git por HTTPS (GitLab, Bitbucket, Azure DevOps, Gitea…): usuário e
 * token (ou senha de app), guardados no cofre do sistema e usados só para esse servidor.
 */
function RemoteCredentialsForm({ url, onSaved }: { url: string; onSaved?: () => void }) {
  const host = new URL(url).hostname;
  const [username, setUsername] = useState(host.includes("gitlab") ? "oauth2" : "");
  const [password, setPassword] = useState("");
  const save = useMutation({
    mutationFn: async () => {
      const res = await desktop!.gitRemote.credentials.set(url, username, password);
      if (!res.ok) throw new Error(res.error ?? "Não foi possível salvar.");
    },
    onSuccess: () => {
      setPassword("");
      toast.success(`Credencial de ${host} salva no cofre do sistema.`);
      onSaved?.();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="grid grid-cols-2 gap-1.5">
        <Input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="usuário"
          aria-label={`Usuário em ${host}`}
          autoComplete="off"
          spellCheck={false}
        />
        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="token ou senha de app"
          aria-label={`Token de ${host}`}
          autoComplete="off"
          spellCheck={false}
        />
      </div>
      <Button
        type="submit"
        size="sm"
        variant="secondary"
        className="w-full"
        loading={save.isPending}
        disabled={!username.trim() || !password.trim()}
      >
        <KeyRound className="h-3.5 w-3.5" /> Salvar credencial de {host}
      </Button>
    </form>
  );
}

/** Linha "credencial de gitlab.com: salva / definir" no painel de um repositório por HTTPS. */
function RemoteCredentials({ url }: { url: string }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const origin = new URL(url).origin;
  const saved = useQuery({
    queryKey: ["git", "credentials"],
    queryFn: () => desktop!.gitRemote.credentials.list(),
  });
  const has = saved.data?.includes(origin) ?? false;
  const refresh = () => qc.invalidateQueries({ queryKey: ["git", "credentials"] });
  return (
    <div className="mb-2 space-y-2 px-1 text-[11.5px] text-fg-subtle">
      <div className="flex items-center gap-1.5">
        <KeyRound className="h-3 w-3 shrink-0" />
        <span className="flex-1 truncate">
          {has
            ? `Credencial de ${new URL(url).hostname} salva`
            : "Sem credencial (só leitura em repositório público)"}
        </span>
        <button className="text-fg-muted hover:text-fg hover:underline" onClick={() => setEditing((v) => !v)}>
          {editing ? "fechar" : has ? "trocar" : "definir"}
        </button>
        {has ? (
          <button
            className="text-fg-muted hover:text-fg hover:underline"
            onClick={() => void desktop?.gitRemote.credentials.remove(url).then(refresh)}
          >
            remover
          </button>
        ) : null}
      </div>
      {editing ? (
        <RemoteCredentialsForm
          url={url}
          onSaved={() => {
            setEditing(false);
            void refresh();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Projeto ainda sem repositório: publicar num repositório novo do GitHub ou ligar a um existente —
 * do GitHub (dono/nome) ou de qualquer servidor Git por HTTPS (GitLab, Bitbucket, Azure DevOps…).
 */
function LinkRepo({ githubConnected }: { githubConnected: boolean }) {
  const { detail, session } = useWs();
  const qc = useQueryClient();
  const slug =
    detail.project.name
      .toLowerCase()
      .replace(/[^a-z0-9_.-]+/g, "-")
      .replace(/^-+|-+$/g, "") || "projeto";
  const [mode, setMode] = useState<"publish" | "link">(githubConnected ? "publish" : "link");
  const [name, setName] = useState(slug);
  const [priv, setPriv] = useState(true);
  const [repo, setRepo] = useState("");
  const [branch, setBranch] = useState("main");
  const [bring, setBring] = useState<"kompil" | "github">("kompil");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [progress, setProgress] = useState<string | null>(null);

  const go = useMutation({
    mutationFn: async () => {
      if (mode === "publish") {
        setProgress("Criando o repositório…");
        const created = await createRepo(name.trim(), priv, detail.project.description ?? undefined);
        await saveLink(detail.project.id, created);
        return { kind: "publish" as const, repo: created.repo };
      }
      const full = await resolveRepoInput(repo, githubConnected);
      if (!full) throw new Error("Use dono/repositório (GitHub) ou o endereço https do repositório.");
      if (isRemoteUrl(full) && username.trim() && password.trim()) {
        const saved = await desktop!.gitRemote.credentials.set(full, username, password);
        if (!saved.ok) throw new Error(saved.error ?? "Não foi possível salvar a credencial.");
        setPassword("");
      }
      const b = branch.trim() || "main";
      if (bring === "github") {
        setProgress(`Trazendo os arquivos de ${remoteName(full)}…`);
        const res = await importInto(session, full, b, (n, total) =>
          setProgress(`Trazendo arquivos… ${n}/${total}`),
        );
        await saveLink(detail.project.id, {
          repo: full,
          branch: b,
          ...{ baseCommit: res.commit, baseFiles: res.baseFiles },
        });
      } else {
        setProgress("Lendo o repositório…");
        const head = await headCommit(full, b);
        if (!head) throw new Error(`A branch ${b} não existe em ${repoLabel(full)}.`);
        const files = await commitFiles(full, head, b);
        await saveLink(detail.project.id, {
          repo: full,
          branch: b,
          baseCommit: head,
          baseFiles: Object.fromEntries(Object.entries(files.files).map(([p, f]) => [p, f.sha])),
        });
      }
      return { kind: "link" as const, repo: full };
    },
    onSuccess: (r) => {
      toast.success(
        r.kind === "publish"
          ? `Repositório ${r.repo} criado. Faça o primeiro commit.`
          : `Projeto ligado a ${repoLabel(r.repo)}.`,
      );
      void qc.invalidateQueries({ queryKey: gitKeys.link(detail.project.id) });
    },
    onError: (err) => toast.error(errorMessage(err)),
    onSettled: () => setProgress(null),
  });

  return (
    <div className="flex h-full flex-col">
      <SectionTitle>Controle do código-fonte</SectionTitle>
      <div className="space-y-3 overflow-y-auto px-4 pb-4 text-[12.5px] text-fg-muted">
        <p className="leading-relaxed">
          Este projeto ainda não está num repositório Git. GitHub, GitLab, Bitbucket, Azure DevOps, Gitea ou
          qualquer servidor por HTTPS.
        </p>
        <div className="grid grid-cols-2 gap-1 rounded-md border border-border p-0.5">
          {(
            [
              ["publish", "Publicar"],
              ["link", "Ligar existente"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setMode(id)}
              className={cn(
                "h-7 rounded-[5px] text-[12px] font-medium transition-colors",
                mode === id ? "bg-bg-emphasis text-fg" : "text-fg-muted hover:text-fg",
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            go.mutate();
          }}
        >
          {mode === "publish" && !githubConnected ? (
            <div className="space-y-1">
              <p className="text-[12px] leading-relaxed">
                Para criar o repositório no GitHub, conecte sua conta. Em outro servidor, crie o repositório
                por lá e use <span className="text-fg">Ligar existente</span>.
              </p>
              <GitHubConnectButton />
            </div>
          ) : mode === "publish" ? (
            <>
              <Field label="Nome do repositório" htmlFor="git-name">
                <Input
                  id="git-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  spellCheck={false}
                />
              </Field>
              <label className="flex items-center justify-between gap-2 text-[12.5px] text-fg">
                Repositório privado
                <Switch checked={priv} onCheckedChange={setPriv} />
              </label>
            </>
          ) : (
            <>
              <Field
                label="Repositório"
                hint="dono/nome do GitHub, ou o endereço https de qualquer servidor Git"
                htmlFor="git-repo"
              >
                <Input
                  id="git-repo"
                  value={repo}
                  onChange={(e) => setRepo(e.target.value)}
                  placeholder="https://gitlab.com/voce/meu-site.git"
                  spellCheck={false}
                />
              </Field>
              {repo.includes("://") || repo.includes("@") || !githubConnected ? (
                <Field
                  label="Credencial (repositório privado)"
                  hint="Fica no cofre do sistema, só para este servidor."
                >
                  <div className="grid grid-cols-2 gap-1.5">
                    <Input
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="usuário"
                      aria-label="Usuário"
                      autoComplete="off"
                      spellCheck={false}
                    />
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="token"
                      aria-label="Token ou senha de app"
                      autoComplete="off"
                      spellCheck={false}
                    />
                  </div>
                </Field>
              ) : null}
              <Field label="Branch" htmlFor="git-branch">
                <Input
                  id="git-branch"
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  spellCheck={false}
                />
              </Field>
              <Field label="Arquivos" htmlFor="git-bring">
                <Select
                  id="git-bring"
                  value={bring}
                  onChange={(e) => setBring(e.target.value as "kompil" | "github")}
                >
                  <option value="kompil">Manter os do Kompil (o commit envia tudo)</option>
                  <option value="github">Trazer os do repositório (substitui o projeto)</option>
                </Select>
              </Field>
            </>
          )}
          <Button
            type="submit"
            variant="primary"
            className={cn("w-full", mode === "publish" && !githubConnected && "hidden")}
            loading={go.isPending}
          >
            {mode === "publish" ? <Upload className="h-3.5 w-3.5" /> : <GitBranch className="h-3.5 w-3.5" />}
            {mode === "publish" ? "Publicar no GitHub" : "Ligar repositório"}
          </Button>
          {progress ? <p className="text-center text-[11.5px] text-fg-subtle">{progress}</p> : null}
        </form>
      </div>
    </div>
  );
}

function resolveConflict(path: string) {
  useGitChanges.setState((s) => ({ conflicts: s.conflicts.filter((p) => p !== path) }));
}

function DiffDialog({
  link,
  change,
  conflict = false,
  onClose,
  onDiscarded,
}: {
  link: GitLink;
  change: Change | null;
  /** Arquivo em conflito no último pull: a base é a versão do GitHub. */
  conflict?: boolean;
  onClose: () => void;
  onDiscarded: () => void;
}) {
  const { session } = useWs();
  const role = useRole();
  const tree = useTree();
  const { data, isLoading, error } = useQuery({
    queryKey: ["git", "diff", link.repo, change?.path, link.baseFiles[change?.path ?? ""]],
    enabled: Boolean(change),
    queryFn: async () => {
      const c = change!;
      const before =
        c.kind === "A"
          ? ""
          : ((await blobText(link.repo, link.baseFiles[c.path]!, link.branch)) ?? "(arquivo binário)");
      const after = c.kind === "D" ? "" : ((await readProjectTexts(session))[c.path] ?? "");
      return {
        before,
        after,
        patch: createTwoFilesPatch(c.path, c.path, before, after, "", "", { context: 3 }),
      };
    },
  });
  const discard = async () => {
    if (!change || !data) return;
    try {
      if (change.kind === "A") deleteFile(session, change.path);
      else await writeFile(session, change.path, data.before);
      toast(
        conflict ? `${change.path}: ficou a versão do GitHub.` : `Alterações de ${change.path} descartadas.`,
      );
      if (conflict) resolveConflict(change.path);
      onDiscarded();
      onClose();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  const fileId = change ? [...tree.paths].find(([, p]) => p === change.path)?.[0] : undefined;
  return (
    <Dialog open={Boolean(change)} onOpenChange={(o) => !o && onClose()}>
      {change ? (
        <DialogContent
          wide
          title={change.path}
          description={
            conflict
              ? "Mudou aqui e no GitHub. À esquerda, a versão do GitHub; à direita, a do Kompil (a que ficou)."
              : `${KIND_LABEL[change.kind]} desde o último commit sincronizado (${link.baseCommit.slice(0, 7)}).`
          }
        >
          <div className="max-h-[60vh] overflow-auto rounded-md border border-border">
            {isLoading ? (
              <div className="flex justify-center p-6">
                <Spinner />
              </div>
            ) : error ? (
              <p className="p-4 text-[12.5px] text-fg-muted">{errorMessage(error)}</p>
            ) : data ? (
              <DiffView patch={data.patch} />
            ) : null}
          </div>
          <DialogFooter>
            {can(role, "write") ? (
              <Button variant="ghost" onClick={() => void discard()} disabled={!data}>
                <RotateCcw className="h-3.5 w-3.5" /> {conflict ? "Usar a do GitHub" : "Descartar alterações"}
              </Button>
            ) : null}
            {conflict ? (
              <Button
                variant="secondary"
                onClick={() => {
                  resolveConflict(change.path);
                  toast(`${change.path}: ficou a versão do Kompil (o próximo commit a envia).`);
                  onClose();
                }}
              >
                <Check className="h-3.5 w-3.5" /> Manter a do Kompil
              </Button>
            ) : null}
            {fileId ? (
              <Button
                variant="secondary"
                onClick={() => {
                  onClose();
                  openAt(fileId, 1);
                }}
              >
                Abrir arquivo
              </Button>
            ) : null}
            <Button variant="primary" onClick={onClose}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      ) : null}
    </Dialog>
  );
}

function ChangeRow({
  change,
  staged,
  canWrite,
  conflict,
  onOpen,
  onOpenFile,
}: {
  change: Change;
  staged: boolean;
  canWrite: boolean;
  conflict?: boolean;
  onOpen: () => void;
  onOpenFile: () => void;
}) {
  const colored = useColoredIcons();
  const name = change.path.split("/").pop()!;
  const dir = change.path.slice(0, -name.length - 1);
  return (
    <div
      className="group flex h-[22px] w-full items-center gap-1.5 pl-5 pr-2 text-[13px] text-fg-muted hover:bg-bg-muted/70 hover:text-fg"
      title={`${change.path} — ${conflict ? "conflito no último pull" : KIND_LABEL[change.kind]}`}
    >
      <button
        onClick={onOpen}
        onDoubleClick={onOpenFile}
        className="flex min-w-0 flex-1 items-center gap-1.5 text-left outline-none focus-visible:underline"
      >
        {colored ? (
          <span className={cn("shrink-0", change.kind === "D" && "opacity-60")}>
            {fileIcon(name, undefined, colored)}
          </span>
        ) : null}
        <span className={cn("truncate", change.kind === "D" && "line-through")}>{name}</span>
        {dir ? <span className="truncate text-[11.5px] text-fg-subtle">{dir}</span> : null}
      </button>
      {canWrite ? (
        <Tip label={staged ? "Tirar da preparação" : "Preparar para o commit"}>
          <button
            onClick={() => (staged ? unstage([change.path]) : stage([change.path]))}
            className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[4px] opacity-0 hover:bg-bg-emphasis focus-visible:opacity-100 group-hover:opacity-100"
            aria-label={staged ? `Tirar ${name} da preparação` : `Preparar ${name}`}
          >
            {staged ? <Minus className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
          </button>
        </Tip>
      ) : null}
      <span
        className={cn(
          "w-3 shrink-0 text-center font-mono text-[11px] font-semibold",
          conflict ? "text-[var(--warning)]" : "text-fg-muted",
        )}
      >
        {conflict ? "!" : change.kind}
      </span>
    </div>
  );
}

function Section({
  title,
  count,
  open,
  onToggle,
  actions,
  children,
}: {
  title: string;
  count?: number;
  open: boolean;
  onToggle: () => void;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="group flex h-7 items-center gap-1 pl-1.5 pr-2 text-[12px] font-medium text-fg-muted">
        <button
          onClick={onToggle}
          className="flex flex-1 items-center gap-1 text-left outline-none focus-visible:underline"
          aria-expanded={open}
        >
          <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-90")} />
          {title}
        </button>
        <span className="flex items-center gap-0.5 opacity-0 focus-within:opacity-100 group-hover:opacity-100">
          {actions}
        </span>
        {count !== undefined ? (
          <span className="rounded-full bg-bg-emphasis px-1.5 font-mono text-[10px] font-normal text-fg-muted">
            {count}
          </span>
        ) : null}
      </div>
      {open ? children : null}
    </div>
  );
}

function HistoryList({ link }: { link: GitLink }) {
  const { data, isLoading, error } = useQuery({
    queryKey: gitKeys.history(link.repo, link.branch),
    queryFn: () => commitHistory(link.repo, link.branch, 20),
    staleTime: 60_000,
  });
  if (isLoading) {
    return (
      <div className="flex justify-center py-2">
        <Spinner className="h-3.5 w-3.5" />
      </div>
    );
  }
  if (error) return <p className="px-5 py-1 text-[12px] text-fg-subtle">{errorMessage(error)}</p>;
  const baseIdx = data?.findIndex((c) => c.sha === link.baseCommit) ?? -1;
  return (
    <ul className="pb-1">
      {(data ?? []).map((c, i) => {
        const pending = baseIdx > 0 && i < baseIdx;
        const isBase = c.sha === link.baseCommit;
        return (
          <li key={c.sha}>
            <button
              onClick={() => desktop?.openExternal(c.url)}
              className="flex w-full items-start gap-2 py-1 pl-5 pr-3 text-left text-[12.5px] hover:bg-bg-muted/70"
              title={`${c.message}\n\n${c.author} · ${c.sha.slice(0, 7)} — abrir no GitHub`}
            >
              <span
                className={cn(
                  "mt-[5px] h-2 w-2 shrink-0 rounded-full border",
                  isBase ? "border-fg bg-fg" : pending ? "border-fg-muted" : "border-fg-subtle bg-fg-subtle",
                )}
                aria-hidden
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-fg">{c.message.split("\n")[0]}</span>
                <span className="block truncate text-[11px] text-fg-subtle">
                  {c.author} · {timeAgo(c.date)} · <span className="font-mono">{c.sha.slice(0, 7)}</span>
                  {isBase ? " · base do Kompil" : pending ? " · falta trazer (pull)" : ""}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function Repository({ link, login }: { link: GitLink; login?: string }) {
  const { detail, session, me } = useWs();
  const role = useRole();
  const tree = useTree();
  const qc = useQueryClient();
  const canWrite = can(role, "write");
  const changes = useGitChanges((s) => s.changes);
  const scanning = useGitChanges((s) => s.scanning);
  const message = useGitChanges((s) => s.message);
  const staged = useGitChanges((s) => s.staged);
  const conflicts = useGitChanges((s) => s.conflicts);
  const setMessage = (m: string) => useGitChanges.setState({ message: m });
  const [viewing, setViewing] = useState<{ change: Change; conflict: boolean } | null>(null);
  const [unlinking, setUnlinking] = useState(false);
  const [byAuthor, setByAuthor] = useState(false);
  const [open, setOpen] = useState({ conflicts: true, staged: true, changes: true, history: false });
  const refreshLink = () => qc.invalidateQueries({ queryKey: gitKeys.link(detail.project.id) });
  const remote = useRemoteStatus(link);
  const host = remoteName(link.repo);
  const github = host === "GitHub";
  const at = github ? "no GitHub" : `em ${host}`;
  const from = github ? "do GitHub" : `de ${host}`;

  const scan = useGitScan(link);
  const commit = useGitCommit(link);

  const sync = useMutation({
    mutationFn: async () => {
      const texts = await readProjectTexts(session);
      const res = await pull(link, session, texts);
      if (res.commit !== link.baseCommit) {
        await saveLink(detail.project.id, { ...link, baseCommit: res.commit, baseFiles: res.baseFiles });
      }
      return res;
    },
    onSuccess: (res) => {
      if (res.commit === link.baseCommit) {
        toast(`Tudo em dia com ${github ? "o GitHub" : host}.`);
        logOutput("Git", `Pull: ${link.branch} já estava em dia.`);
      } else {
        const text =
          `${res.applied} ${res.applied === 1 ? "arquivo atualizado" : "arquivos atualizados"} ${from}.` +
          (res.conflicts.length
            ? ` ${res.conflicts.length} em conflito ficaram com a versão do Kompil: ${res.conflicts.slice(0, 3).join(", ")}.`
            : "");
        toast.success(text);
        logOutput(
          "Git",
          `Pull de ${link.branch} (${res.commit.slice(0, 7)}): ${text}`,
          res.conflicts.length ? "warn" : "info",
        );
        logSessionEvent({
          kind: "git",
          who: me.user.name,
          text: `trouxe ${link.branch} ${from} (${res.commit.slice(0, 7)})`,
        });
      }
      useGitChanges.setState({ conflicts: res.conflicts });
      void refreshLink();
      void qc.invalidateQueries({ queryKey: ["git", "remote", link.repo] });
      void qc.invalidateQueries({ queryKey: gitKeys.history(link.repo, link.branch) });
    },
    onError: (err) => {
      toast.error(errorMessage(err));
      logOutput("Git", `Pull falhou: ${errorMessage(err)}`, "error");
    },
  });

  const unlink = useMutation({
    mutationFn: () => del(`/projects/${detail.project.id}/git`),
    onSuccess: () => {
      setUnlinking(false);
      toast(`Repositório desligado do projeto. Nada foi apagado ${at}.`);
      logOutput("Git", `Repositório ${link.repo} desligado do projeto.`);
      void refreshLink();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const busy = commit.isPending || sync.isPending;
  const all = changes ?? [];
  const stagedSetNow = new Set(staged);
  const conflictSet = new Set(conflicts);
  const stagedChanges = all.filter((c) => stagedSetNow.has(c.path));
  const pending = all.filter((c) => !stagedSetNow.has(c.path) && !conflictSet.has(c.path));
  const conflictChanges = all.filter((c) => conflictSet.has(c.path));
  const commitCount = stagedChanges.length || all.length;
  const canCommit = canWrite && all.length > 0 && message.trim().length > 0 && !busy;
  const idOf = (path: string) => [...tree.paths].find(([, p]) => p === path)?.[0];
  const behind = remote.data?.behind ?? 0;
  const toggle = (k: keyof typeof open) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  const row = (c: Change, isStaged: boolean, conflict = false) => (
    <ChangeRow
      key={`${isStaged ? "s" : conflict ? "c" : "u"}:${c.path}`}
      change={c}
      staged={isStaged}
      canWrite={canWrite}
      conflict={conflict}
      onOpen={() => setViewing({ change: c, conflict })}
      onOpenFile={() => {
        const id = idOf(c.path);
        if (id) openAt(id, 1);
      }}
    />
  );
  const smallAction = (label: string, onClick: () => void, icon: ReactNode) => (
    <Tip label={label}>
      <button
        onClick={onClick}
        className="flex h-[18px] w-[18px] items-center justify-center rounded-[4px] text-fg-muted hover:bg-bg-emphasis hover:text-fg"
        aria-label={label}
      >
        {icon}
      </button>
    </Tip>
  );

  return (
    <div className="flex h-full flex-col">
      <SectionTitle
        actions={
          <>
            <Tip label="Atualizar alterações">
              <button
                onClick={() => {
                  void scan();
                  void qc.invalidateQueries({ queryKey: ["git", "remote", link.repo] });
                }}
                className="flex h-[22px] w-[22px] items-center justify-center rounded-[4px] text-fg-muted hover:bg-bg-emphasis hover:text-fg"
                aria-label="Atualizar alterações"
              >
                {scanning ? <Spinner className="h-3.5 w-3.5" /> : <RefreshCw className="h-3.5 w-3.5" />}
              </button>
            </Tip>
            <Menu>
              <MenuTrigger asChild>
                <button
                  className="flex h-[22px] w-[22px] items-center justify-center rounded-[4px] text-fg-muted hover:bg-bg-emphasis hover:text-fg"
                  aria-label="Mais ações do Git"
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </button>
              </MenuTrigger>
              <MenuContent align="end">
                <MenuItem
                  icon={<ArrowDownToLine className="h-3.5 w-3.5" />}
                  disabled={!canWrite || busy}
                  onSelect={() => sync.mutate()}
                >
                  Pull (trazer {from})
                </MenuItem>
                <MenuItem
                  icon={<ExternalLink className="h-3.5 w-3.5" />}
                  onSelect={() => desktop?.openExternal(repoWebUrl(link.repo, link.branch))}
                >
                  Abrir {at}
                </MenuItem>
                <MenuSeparator />
                <MenuItem
                  icon={<Unlink className="h-3.5 w-3.5" />}
                  disabled={!canWrite}
                  onSelect={() => setUnlinking(true)}
                >
                  Desligar repositório…
                </MenuItem>
              </MenuContent>
            </Menu>
          </>
        }
      >
        Controle do código-fonte
      </SectionTitle>
      <div className="px-3 pb-2">
        <div className="mb-2 flex items-center gap-1.5 px-1 text-[12px] text-fg-muted">
          {github ? (
            <GithubMark className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <Globe className="h-3.5 w-3.5 shrink-0" />
          )}
          <span className="truncate text-fg" title={link.repo}>
            {repoLabel(link.repo)}
          </span>
          <span className="flex shrink-0 items-center gap-1 rounded-[4px] border border-border px-1.5 font-mono text-[10.5px]">
            <GitBranch className="h-3 w-3" />
            {link.branch}
          </span>
          <span
            className="ml-auto shrink-0 text-[11px] text-fg-subtle"
            title={
              remote.error
                ? `Não foi possível consultar ${github ? "o GitHub" : host} agora.`
                : remote.data?.diverged
                  ? `A branch foi reescrita ${at} (force push): a base do Kompil não está mais no histórico.`
                  : behind
                    ? `${behind} ${behind === 1 ? "commit novo" : "commits novos"} ${at} — faça o pull.`
                    : `Base do Kompil igual à branch ${at}.`
            }
          >
            {remote.isLoading
              ? "…"
              : remote.error
                ? "?"
                : remote.data?.diverged
                  ? "divergiu"
                  : behind
                    ? `↓ ${behind}`
                    : "em dia"}
          </span>
        </div>
        {isRemoteUrl(link.repo) ? <RemoteCredentials url={link.repo} /> : null}
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && canCommit) {
              e.preventDefault();
              commit.mutate();
            }
          }}
          rows={2}
          disabled={!canWrite}
          placeholder={`Mensagem (Ctrl+Enter para commit em "${link.branch}")`}
          className="min-h-[52px] resize-none text-[12.5px]"
          aria-label="Mensagem do commit"
        />
        <div className="mt-2 flex gap-1">
          <Button
            variant="primary"
            className="flex-1 justify-center"
            disabled={!canCommit}
            loading={commit.isPending}
            onClick={() => commit.mutate()}
          >
            <Check className="h-3.5 w-3.5" />
            {stagedChanges.length ? `Commit e push (${commitCount} preparados)` : "Commit e push"}
          </Button>
          <Tip
            label={
              behind
                ? `Pull: trazer ${behind} ${behind === 1 ? "commit" : "commits"} ${from}`
                : `Pull: trazer commits novos ${from}`
            }
          >
            <Button
              variant="secondary"
              size="icon"
              disabled={!canWrite || busy}
              onClick={() => sync.mutate()}
              aria-label="Pull"
            >
              {sync.isPending ? (
                <Spinner className="h-3.5 w-3.5" />
              ) : (
                <ArrowDownToLine className="h-3.5 w-3.5" />
              )}
            </Button>
          </Tip>
        </div>
        {all.length && !stagedChanges.length && canWrite ? (
          <p className="mt-1.5 px-0.5 text-[11px] text-fg-subtle">
            Nada preparado: o commit leva todas as alterações. Use + para escolher.
          </p>
        ) : null}
        {all.length && canWrite && !conflictChanges.length ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              className="mt-1 w-full justify-center"
              disabled={busy || behind > 0}
              onClick={() => setByAuthor(true)}
            >
              <Users className="h-3.5 w-3.5" /> Organizar em commits por autor
            </Button>
            {byAuthor ? (
              <AuthorCommitsDialog open={byAuthor} onOpenChange={setByAuthor} link={link} changes={all} />
            ) : null}
          </>
        ) : null}
        {behind > 0 ? (
          <p className="mt-1.5 px-0.5 text-[11px] text-fg-muted">
            {github ? "O GitHub" : host} tem {behind} {behind === 1 ? "commit" : "commits"} que o Kompil ainda
            não trouxe: faça o pull antes do push.
          </p>
        ) : null}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto border-t border-border pb-3 pt-1">
        {!changes ? (
          <div className="flex justify-center py-3">
            <Spinner className="h-3.5 w-3.5" />
          </div>
        ) : null}
        {conflictChanges.length ? (
          <Section
            title="Conflitos"
            count={conflictChanges.length}
            open={open.conflicts}
            onToggle={() => toggle("conflicts")}
          >
            <p className="px-5 pb-1 text-[11.5px] leading-relaxed text-fg-subtle">
              Mudaram aqui e {at}. Ficou a versão do Kompil; abra para comparar e escolher.
            </p>
            {conflictChanges.map((c) => row(c, stagedSetNow.has(c.path), true))}
          </Section>
        ) : null}
        {stagedChanges.length ? (
          <Section
            title="Alterações preparadas"
            count={stagedChanges.length}
            open={open.staged}
            onToggle={() => toggle("staged")}
            actions={
              canWrite
                ? smallAction(
                    "Tirar tudo da preparação",
                    () => unstage(staged),
                    <Minus className="h-3 w-3" />,
                  )
                : null
            }
          >
            {stagedChanges.map((c) => row(c, true))}
          </Section>
        ) : null}
        {changes ? (
          <Section
            title="Alterações"
            count={pending.length}
            open={open.changes}
            onToggle={() => toggle("changes")}
            actions={
              canWrite && pending.length
                ? smallAction(
                    "Preparar tudo",
                    () => stage(pending.map((c) => c.path)),
                    <Plus className="h-3 w-3" />,
                  )
                : null
            }
          >
            {pending.length ? (
              pending.map((c) => row(c, false))
            ) : (
              <p className="px-5 py-1.5 text-[12px] text-fg-subtle">
                {all.length ? "Tudo preparado para o commit." : "Nada mudou desde o último commit."}
              </p>
            )}
          </Section>
        ) : null}
        <Section title="Histórico" open={open.history} onToggle={() => toggle("history")}>
          <HistoryList link={link} />
        </Section>
        <p className="mt-3 px-5 text-[11px] leading-relaxed text-fg-subtle">
          Base: {link.baseCommit.slice(0, 7)}
          {link.updatedByHandle ? ` · por @${link.updatedByHandle}` : ""}
          {login && github ? ` · você no GitHub: @${login}` : ""}
        </p>
      </div>
      <DiffDialog
        link={link}
        change={viewing?.change ?? null}
        conflict={viewing?.conflict ?? false}
        onClose={() => setViewing(null)}
        onDiscarded={() => void scan()}
      />
      <Dialog open={unlinking} onOpenChange={setUnlinking}>
        <DialogContent
          title="Desligar o repositório?"
          description={`O projeto continua no Kompil e o repositório continua ${at}; só a ligação entre os dois é removida (para o time todo).`}
        >
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUnlinking(false)}>
              Cancelar
            </Button>
            <Button variant="danger" loading={unlink.isPending} onClick={() => unlink.mutate()}>
              <Unlink className="h-3.5 w-3.5" /> Desligar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Painel "Controle do código-fonte" (extensão Git). Precisa de conta no Kompil e do app desktop. */
export function GitPanel() {
  const { detail, me } = useWs();
  const guest = me.user.isGuest;
  const account = useGitAccount(!guest);
  const link = useGitLink(detail.project.id, !guest && Boolean(desktop));

  if (!desktop) {
    return (
      <Gate title="Git no Kompil Desktop">
        <p>
          Commit, push e pull com o GitHub ficam no app desktop, onde o seu token é guardado com segurança.
        </p>
        <a href={DOWNLOAD_URL} className="mt-3 inline-block text-fg underline underline-offset-4">
          Baixar o Kompil Desktop
        </a>
      </Gate>
    );
  }
  if (guest) {
    return (
      <Gate title="Entre para usar o Git">
        <p>O Git (commit, push e pull com o GitHub) é ligado à sua conta do Kompil. É grátis.</p>
        <Button
          variant="primary"
          className="mt-3 w-full"
          onClick={() => useDesktopUi.getState().set({ login: "Entre para usar o Git com o GitHub." })}
        >
          <LogIn className="h-3.5 w-3.5" /> Entrar
        </Button>
      </Gate>
    );
  }
  if (account.isLoading || link.isLoading) {
    return (
      <Gate title="Controle do código-fonte">
        <Spinner />
      </Gate>
    );
  }
  if (!link.data) return <LinkRepo githubConnected={Boolean(account.data?.connected)} />;
  if (!isRemoteUrl(link.data.repo) && !account.data?.connected) return <ConnectAccount />;
  return <Repository link={link.data} login={account.data?.login} />;
}

/**
 * Importação pedida por Arquivo › Importar repositório (/p/:id?importar=dono/repo&branch=main, ou
 * importar=https://servidor/caminho):
 * traz os arquivos para o projeto recém-criado e liga o repositório.
 */
export function useRepoImport() {
  const { session, detail } = useWs();
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const repo = params.get("importar");
  const branch = params.get("branch") || "main";
  const started = useRef(false);
  useEffect(() => {
    if (!repo || started.current || !desktop) return;
    started.current = true;
    setParams(
      (p) => {
        p.delete("importar");
        p.delete("branch");
        return p;
      },
      { replace: true },
    );
    const label = repoLabel(repo);
    const id = toast.loading(`Importando ${label}…`);
    void (async () => {
      try {
        // Espera a árvore chegar do servidor (e o papel de editor) antes de escrever.
        for (let i = 0; i < 150 && !(session.treeProvider.isSynced && session.canWrite); i++) {
          await new Promise((r) => setTimeout(r, 100));
        }
        const res = await importInto(session, repo, branch, (n, total) =>
          toast.loading(`Importando ${label}… ${n}/${total}`, { id }),
        );
        await saveLink(detail.project.id, { repo, branch, baseCommit: res.commit, baseFiles: res.baseFiles });
        await qc.invalidateQueries({ queryKey: gitKeys.link(detail.project.id) });
        toast.success(`${label} importado (${Object.keys(res.baseFiles).length} arquivos).`, { id });
      } catch (err) {
        toast.error(errorMessage(err), { id });
      }
    })();
  }, [repo, branch, session, detail.project.id, qc, setParams]);
}

/**
 * Liga ao projeto o repositório de uma pasta com .git (o remoto "origin" e a branch dela). A base é
 * o último commit do servidor; o que a pasta tem de diferente aparece como alteração para commitar.
 */
export async function connectFolderGit(
  projectId: string,
  remote: string,
  branch: string,
  qc: ReturnType<typeof useQueryClient>,
) {
  if (!desktop) return;
  const id = toast.loading("Ligando o repositório da pasta…");
  try {
    const account = await desktop.git.status().catch(() => ({ connected: false }));
    const repo = await resolveRepoInput(remote, account.connected);
    if (!repo) throw new Error(`Endereço do repositório não reconhecido: ${remote}`);
    const head = await headCommit(repo, branch);
    if (!head) throw new Error(`A branch ${branch} ainda não existe no servidor.`);
    const files = await commitFiles(repo, head, branch);
    await saveLink(projectId, {
      repo,
      branch,
      baseCommit: head,
      baseFiles: Object.fromEntries(Object.entries(files.files).map(([p, f]) => [p, f.sha])),
    });
    await qc.invalidateQueries({ queryKey: gitKeys.link(projectId) });
    toast.success(`Ligado a ${repoLabel(repo)} (${branch})`, {
      id,
      description:
        account.connected || isRemoteUrl(repo) ? undefined : "Conecte o GitHub para fazer commit e push.",
    });
  } catch (err) {
    const message = errorMessage(err);
    toast.error("Não deu para ligar o repositório agora", {
      id,
      description: /401|login|credencial/i.test(message)
        ? "O repositório é privado: conecte sua conta no painel Git e ligue de novo."
        : message,
      action: { label: "Abrir o Git", onClick: () => useWorkspace.getState().setPanel("git") },
    });
  }
}

/**
 * Projeto criado de uma pasta do computador com .git (/p/:id?pasta=1&git=<remoto>&branch=):
 * quando a cópia local termina de subir os arquivos, liga o repositório. A base é o último commit
 * do servidor; o que a pasta tem de diferente aparece como alteração para commitar.
 */
export function useFolderImport() {
  const { detail } = useWs();
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const remote = params.get("git");
  const branch = params.get("branch") || "main";
  const imported = params.get("pasta") === "1";
  const localState = useLocalCopy((s) => s.state);
  const started = useRef(false);
  useEffect(() => {
    if (!imported || started.current || localState !== "on") return;
    started.current = true;
    setParams(
      (p) => {
        p.delete("pasta");
        p.delete("git");
        p.delete("branch");
        return p;
      },
      { replace: true },
    );
    toast.success("Pasta importada", {
      description: "Os arquivos estão no projeto e a pasta segue sincronizada.",
    });
    if (remote) void connectFolderGit(detail.project.id, remote, branch, qc);
  }, [imported, remote, branch, localState, detail.project.id, qc, setParams]);
}

/**
 * Rodapé "Git" do explorador: branch, mensagem e commit à mão, sem abrir o painel. Sem repositório
 * (ou sem conta), mostra o atalho para configurar.
 */
export function GitFooter() {
  const { detail, me } = useWs();
  const role = useRole();
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem("kompil.gitFooter") !== "0";
    } catch {
      return true;
    }
  });
  const toggle = () => {
    setOpen((v) => {
      try {
        localStorage.setItem("kompil.gitFooter", v ? "0" : "1");
      } catch {
        // sem armazenamento
      }
      return !v;
    });
  };
  const guest = me.user.isGuest;
  const account = useGitAccount(!guest && open);
  const link = useGitLink(detail.project.id, !guest && Boolean(desktop) && open);
  const ready = Boolean(link.data && (isRemoteUrl(link.data.repo) || account.data?.connected));
  if (!desktop) return null;
  return (
    <div className="shrink-0 border-t border-panel-border">
      <button
        onClick={toggle}
        className="flex h-9 w-full items-center gap-1 px-2.5 text-[12.5px] font-semibold text-fg-muted hover:text-fg"
        aria-expanded={open}
      >
        <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-90")} />
        Git
        {ready && link.data ? (
          <span className="ml-auto flex items-center gap-1 font-normal normal-case tracking-normal text-fg-subtle">
            <GitBranch className="h-3 w-3" /> {link.data.branch}
          </span>
        ) : null}
      </button>
      {open ? (
        guest ? (
          <div className="px-3 pb-3 text-[12px] text-fg-subtle">
            <button
              className="underline-offset-4 hover:text-fg hover:underline"
              onClick={() => useDesktopUi.getState().set({ login: "Entre para usar o Git com o GitHub." })}
            >
              Entre para usar o Git
            </button>
          </div>
        ) : ready && link.data ? (
          <GitFooterRepo link={link.data} canWrite={can(role, "write")} />
        ) : (
          <div className="px-3 pb-3">
            <Button
              size="sm"
              variant="secondary"
              className="w-full"
              loading={account.isLoading || link.isLoading}
              onClick={() => useWorkspace.getState().setPanel("git")}
            >
              <GitBranch className="h-3.5 w-3.5" /> Ligar a um repositório
            </Button>
          </div>
        )
      ) : null}
    </div>
  );
}

function GitFooterRepo({ link, canWrite }: { link: GitLink; canWrite: boolean }) {
  useGitScan(link);
  const commit = useGitCommit(link);
  const changes = useGitChanges((s) => s.changes);
  const scanning = useGitChanges((s) => s.scanning);
  const message = useGitChanges((s) => s.message);
  const stagedCount = useGitChanges((s) => s.staged.length);
  const count = changes?.length ?? 0;
  const can = canWrite && count > 0 && message.trim().length > 0 && !commit.isPending;
  return (
    <div className="space-y-2 px-3 pb-3">
      <div className="flex gap-1.5">
        <Input
          value={message}
          onChange={(e) => useGitChanges.setState({ message: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter" && can) {
              e.preventDefault();
              commit.mutate();
            }
          }}
          disabled={!canWrite}
          placeholder="Mensagem do commit…"
          className="h-[30px] text-[12.5px]"
          aria-label="Mensagem do commit"
        />
        <Tip
          label={
            stagedCount
              ? `Commit e push em ${link.branch} (${stagedCount} preparados)`
              : `Commit e push em ${link.branch} (todas as alterações)`
          }
        >
          <Button
            size="icon"
            variant="secondary"
            className="h-[30px] w-[30px] shrink-0"
            disabled={!can}
            loading={commit.isPending}
            onClick={() => commit.mutate()}
            aria-label="Commit e push"
          >
            {commit.isPending ? null : <Check className="h-3.5 w-3.5" />}
          </Button>
        </Tip>
      </div>
      <button
        onClick={() => useWorkspace.getState().setPanel("git")}
        className="flex w-full items-center gap-1.5 rounded-md px-1 py-0.5 text-[12.5px] text-fg-muted hover:text-fg"
      >
        {scanning ? <Spinner className="h-3 w-3" /> : <RefreshCw className="h-3 w-3" />}
        Alterações
        <span className="ml-auto rounded-full bg-bg-emphasis px-1.5 text-[11px] text-fg-muted">
          {changes ? count : "…"}
        </span>
      </button>
    </div>
  );
}
